import React, { useState, useEffect } from "react";
import "./bookedorderConfirm.css";

const PaymentConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  orderTotal = 15000,
  paymentType,
  setPaymentType,
  initialPayment,
  setInitialPayment,
  dueDate,
  setDueDate,
  balance,
  setBalance,
  currency,
}) => {
  const [error, setError] = useState("");

  // Set minimum date (tomorrow)
  useEffect(() => {
    if (paymentType === "partial") {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const minDate = tomorrow.toISOString().split("T")[0];
      setDueDate(minDate);
    }
  }, [paymentType]);
  useEffect(() => {
    console.log("dueDate changed:", dueDate);
  }, [dueDate]);

  // Calculate balance
  useEffect(() => {
    const payment = parseFloat(initialPayment) || 0;
    setBalance(Math.max(0, orderTotal - payment));
  }, [initialPayment, orderTotal]);

  // Validate initial payment
  const validateInitialPayment = () => {
    const payment = parseFloat(initialPayment) || 0;

    if (payment <= 0) {
      setError("Please enter initial payment amount");
      return false;
    }

    if (payment >= orderTotal) {
      setError("Initial payment cannot equal or exceed order total");
      return false;
    }

    setError("");
    return true;
  };

  // Handle payment type change
  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
    if (type === "full") {
      setInitialPayment("");
      setDueDate("");
      setError("");
    }
  };

  // Handle initial payment input
  const handleInitialPaymentChange = (e) => {
    setInitialPayment(e.target.value);
    setError("");
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `${currency} ${amount.toLocaleString("no-NO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (paymentType === "full") {
      const paymentData = {
        type: "full",
        amount: orderTotal,
        orderTotal: orderTotal,
        balance: 0,
      };
      onConfirm(paymentData);
      handleClose();
    } else if (paymentType === "partial") {
      if (!validateInitialPayment()) {
        return;
      }

      if (!dueDate) {
        setError("Please select a due date");
        return;
      }

      const paymentData = {
        type: "partial",
        orderTotal: orderTotal,
        initialPayment: parseFloat(initialPayment),
        balance: balance,
        dueDate: dueDate,
      };
      onConfirm(paymentData);
      handleClose();
    }
  };

  // Handle close
  const handleClose = () => {
    setPaymentType("full");
    setInitialPayment("");
    setDueDate("");
    setError("");
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={`sl-payment-modal-overlay ${isOpen ? "sl-payment-active" : ""}`} onClick={handleOverlayClick}>
      <div className='sl-payment-modal'>
        {/* Header */}
        <div className='sl-payment-modal-header'>
          <div className='sl-payment-icon-wrapper'>
            <svg className='sl-payment-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' />
            </svg>
          </div>
          <h2 className='sl-payment-modal-title'>Payment Confirmation</h2>
          <p className='sl-payment-modal-subtitle'>Please confirm payment status before proceeding</p>
        </div>

        {/* Warning Alert */}
        <div className='sl-payment-alert'>
          <svg className='sl-payment-alert-icon' viewBox='0 0 24 24' fill='currentColor'>
            <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z' />
          </svg>
          <span className='sl-payment-alert-text'>Don't confirm order if customer has not yet paid. Note: items are marked as supplied</span>
        </div>

        {/* Payment Options */}
        <div className='sl-payment-options'>
          <label className='sl-payment-option'>
            <input
              type='radio'
              name='paymentType'
              value='full'
              className='sl-payment-radio'
              checked={paymentType === "full"}
              onChange={() => handlePaymentTypeChange("full")}
            />
            <div className='sl-payment-option-content'>
              <div className='sl-payment-option-icon'>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </div>
              <div className='sl-payment-option-text'>
                <span className='sl-payment-option-title'>Full Payment</span>
                <span className='sl-payment-option-desc'>Customer has paid in full</span>
              </div>
            </div>
          </label>

          <label className='sl-payment-option'>
            <input
              type='radio'
              name='paymentType'
              value='partial'
              className='sl-payment-radio'
              checked={paymentType === "partial"}
              onChange={() => handlePaymentTypeChange("partial")}
            />
            <div className='sl-payment-option-content'>
              <div className='sl-payment-option-icon'>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </div>
              <div className='sl-payment-option-text'>
                <span className='sl-payment-option-title'>Part Payment (Credit)</span>
                <span className='sl-payment-option-desc'>Customer will pay balance later</span>
              </div>
            </div>
          </label>
        </div>

        {/* Part Payment Details */}
        {paymentType === "partial" && (
          <div className='sl-payment-details'>
            <div className='sl-payment-form'>
              <div className='sl-payment-form-row'>
                <div className='sl-payment-form-group'>
                  <label className='sl-payment-label'>Initial Payment</label>
                  <div className='sl-payment-input-wrapper'>
                    <span className='sl-payment-currency'>{currency}</span>
                    <input type='number' className='sl-payment-input' placeholder='0' min='0' value={initialPayment} onChange={handleInitialPaymentChange} />
                  </div>
                  <span className='sl-payment-error'>{error}</span>
                </div>

                <div className='sl-payment-form-group'>
                  <label className='sl-payment-label'>Balance Due Date</label>
                  <input
                    type='date'
                    className='sl-payment-input sl-payment-date-input'
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
                  />
                </div>
              </div>

              {/* Payment Summary */}
              <div className='sl-payment-summary'>
                <div className='sl-payment-summary-row'>
                  <span className='sl-payment-summary-label'>Order Total:</span>
                  <span className='sl-payment-summary-value'>{formatCurrency(orderTotal)}</span>
                </div>
                <div className='sl-payment-summary-row'>
                  <span className='sl-payment-summary-label'>Initial Payment:</span>
                  <span className='sl-payment-summary-value sl-payment-highlight'>{formatCurrency(parseFloat(initialPayment) || 0)}</span>
                </div>
                <div className='sl-payment-summary-divider'></div>
                <div className='sl-payment-summary-row sl-payment-summary-balance'>
                  <span className='sl-payment-summary-label'>Balance Remaining:</span>
                  <span className='sl-payment-summary-value'>{formatCurrency(balance)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className='sl-payment-actions'>
          <button className='sl-payment-btn sl-payment-btn-cancel' onClick={handleClose}>
            Cancel
          </button>
          <button className='sl-payment-btn sl-payment-btn-confirm' onClick={handleConfirm}>
            <svg className='sl-payment-btn-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
            Confirm Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmationModal;
